import log from 'loglevel';

import type DxfArrayScanner from '../DxfArrayScanner';
import type { IGroup, GroupValue } from '../DxfArrayScanner';
import { groupIs, logUnhandledGroup } from '../ParseHelpers';
import { DXFSymbol } from '../constants';
import type { ILayer } from './tables/layer';
import { parseLayers, serializeLayers } from './tables/layer';
import type { ILineType } from './tables/ltype';
import { parseLineTypes, serializeLineTypes } from './tables/ltype';
import type { ISomeTable } from './tables/someTable';
import { parseSomeTable, serializeSomeTable } from './tables/someTable';
import type { IStyle } from './tables/style';
import { parseStyles, serializeStyles } from './tables/style';
import type { IViewPort } from './tables/vport';
import { parseViewPortRecords, serializeViewPorts } from './tables/vport';

export type DXFTableSymbol =
  | DXFSymbol.VPort
  | DXFSymbol.LType
  | DXFSymbol.Layer
  | DXFSymbol.Style
  //Limited support:
  | DXFSymbol.AppId
  | DXFSymbol.BlockRecord
  | DXFSymbol.DimStyle
  | DXFSymbol.UCS
  | DXFSymbol.View;

export const enum TableKey {
  ViewPorts = 'viewPorts',
  LineTypes = 'lineTypes',
  Layers = 'layers',
  Styles = 'styles',
  //Limited support:
  AppId = 'appId',
  BlockRecord = 'blockRecord',
  DimStyle = 'dimStyle',
  UCS = 'UCS',
  View = 'view',
}

export const enum TableName {
  ViewPort = 'viewPort',
  LineType = 'lineType',
  Layer = 'layer',
  Style = 'style',

  //Limited support:
  AppId = 'appId',
  BlockRecord = 'blockRecord',
  DimStyle = 'dimStyle',
  UCS = 'UCS',
  View = 'view',
}

export type ApplicationDefinedGroups = Record<
  string,
  Record<string, GroupValue>
>;

export const parseApplicationGroup = (
  scanner: DxfArrayScanner,
): Record<string, GroupValue> => {
  const groups: Record<string, GroupValue> = {};
  let curr = scanner.next();
  while (curr.code != 102) {
    groups[curr.code] = curr.value;
    curr = scanner.next();
  }
  return groups;
};

export const serializeApplicationDefinedGroups = function* (
  groups: ApplicationDefinedGroups,
): IterableIterator<string> {
  for (const [name, group] of Object.entries(groups)) {
    yield '102';
    yield `{${name}`;
    for (const [code, value] of Object.entries(group)) {
      yield code;
      yield `${value}`;
    }
    yield '102';
    yield '}';
  }
};

type TableKeyFromDXFSymbol<T extends DXFTableSymbol> = T extends DXFSymbol.VPort
  ? TableKey.ViewPorts
  : T extends DXFSymbol.LType
  ? TableKey.LineTypes
  : T extends DXFSymbol.Layer
  ? TableKey.Layers
  : T extends DXFSymbol.Style
  ? TableKey.Styles
  : T extends DXFSymbol.AppId
  ? TableKey.AppId
  : T extends DXFSymbol.BlockRecord
  ? TableKey.BlockRecord
  : T extends DXFSymbol.DimStyle
  ? TableKey.DimStyle
  : T extends DXFSymbol.UCS
  ? TableKey.UCS
  : T extends DXFSymbol.View
  ? TableKey.View
  : never;

type TableNameFromDXFSymbol<T extends DXFTableSymbol> =
  T extends DXFSymbol.VPort
    ? TableName.ViewPort
    : T extends DXFSymbol.LType
    ? TableName.LineType
    : T extends DXFSymbol.Layer
    ? TableName.Layer
    : T extends DXFSymbol.Style
    ? TableName.Style
    : T extends DXFSymbol.AppId
    ? TableName.AppId
    : T extends DXFSymbol.BlockRecord
    ? TableName.BlockRecord
    : T extends DXFSymbol.DimStyle
    ? TableName.DimStyle
    : T extends DXFSymbol.UCS
    ? TableName.UCS
    : T extends DXFSymbol.View
    ? TableName.View
    : never;

type TableValueFromDXFSymbol<T extends DXFTableSymbol> =
  T extends DXFSymbol.VPort
    ? IViewPort[]
    : T extends DXFSymbol.LType
    ? Record<string, ILineType>
    : T extends DXFSymbol.Layer
    ? Record<string, ILayer>
    : T extends DXFSymbol.Style
    ? Record<string, IStyle>
    : T extends DXFSymbol.AppId
    ? ISomeTable
    : T extends DXFSymbol.BlockRecord
    ? ISomeTable
    : T extends DXFSymbol.DimStyle
    ? ISomeTable
    : T extends DXFSymbol.UCS
    ? ISomeTable
    : T extends DXFSymbol.View
    ? ISomeTable
    : never;

export type TableDefinition<T extends DXFTableSymbol> = {
  tableRecordsProperty: TableKeyFromDXFSymbol<T>;
  tableName: TableNameFromDXFSymbol<T>;
  dxfSymbolName: T;
  parseTableRecords(scanner: DxfArrayScanner): TableValueFromDXFSymbol<T>;
  serializeTable(items: TableValueFromDXFSymbol<T>): IterableIterator<string>;
};

export type IViewPortTableDefinition = TableDefinition<DXFSymbol.VPort>;
export type ILineTypeTableDefinition = TableDefinition<DXFSymbol.LType>;
export type ILayerTableDefinition = TableDefinition<DXFSymbol.Layer>;
export type IStyleTableDefinition = TableDefinition<DXFSymbol.Style>;
export type IAppIdTableDefinition = TableDefinition<DXFSymbol.AppId>;
export type IBlockRecordTableDefinition =
  TableDefinition<DXFSymbol.BlockRecord>;
export type IDimStyleRecordTableDefinition =
  TableDefinition<DXFSymbol.DimStyle>;
export type IUCSRecordTableDefinition = TableDefinition<DXFSymbol.UCS>;
export type IViewRecordTableDefinition = TableDefinition<DXFSymbol.View>;

export type ITableDefinitions = {
  [T in DXFTableSymbol]: TableDefinition<T>;
};

export type IBaseTable = {
  handle?: string;
  ownerHandle?: string;
  maxEntities?: number; //The table's length or number of keys should equal maxEntities, but this is not always the case
  applicationGroups?: ApplicationDefinedGroups;

  //Note: subMarkers insertion order must be maintained when serializing
  //subMarkers are necessary for eDrawings to import dxf we serialize
  //Tables use 1 or 2 subMarker types. 3rd and 4th are added for insurance
  subMarkerCount?: number;
  subMarker1?: string;
  subMarker2?: string;
  subMarker3?: string;
  subMarker4?: string;
};

export type ITable<T extends DXFTableSymbol> = IBaseTable & {
  [TABLEKEY in TableKeyFromDXFSymbol<T>]: TableValueFromDXFSymbol<T>;
};

export type IViewPortTable = ITable<DXFSymbol.VPort>;
export type ILineTypesTable = ITable<DXFSymbol.LType>;
export type ILayersTable = ITable<DXFSymbol.Layer>;
export type IStylesTable = ITable<DXFSymbol.Style>;
export type IAppIdTable = ITable<DXFSymbol.AppId>;
export type IBlockRecordTable = ITable<DXFSymbol.BlockRecord>;
export type IDimStyleTable = ITable<DXFSymbol.DimStyle>;
export type IUCSTable = ITable<DXFSymbol.UCS>;
export type IViewTable = ITable<DXFSymbol.View>;

export type ITables = {
  [TableName.ViewPort]?: IViewPortTable;
  [TableName.LineType]?: ILineTypesTable;
  [TableName.Layer]?: ILayersTable;
  [TableName.Style]?: IStylesTable;
  //Limited Support:
  [TableName.AppId]?: IAppIdTable;
  [TableName.BlockRecord]?: IBlockRecordTable;
  [TableName.DimStyle]?: IDimStyleTable;
  [TableName.UCS]?: IUCSTable;
  [TableName.View]?: IViewTable;
};

const tableDefinitions: ITableDefinitions = {
  [DXFSymbol.VPort]: {
    tableRecordsProperty: TableKey.ViewPorts,
    tableName: TableName.ViewPort,
    dxfSymbolName: DXFSymbol.VPort,
    parseTableRecords: parseViewPortRecords,
    serializeTable: serializeViewPorts,
  },
  [DXFSymbol.LType]: {
    tableRecordsProperty: TableKey.LineTypes,
    tableName: TableName.LineType,
    dxfSymbolName: DXFSymbol.LType,
    parseTableRecords: parseLineTypes,
    serializeTable: serializeLineTypes,
  },
  [DXFSymbol.Layer]: {
    tableRecordsProperty: TableKey.Layers,
    tableName: TableName.Layer,
    dxfSymbolName: DXFSymbol.Layer,
    parseTableRecords: parseLayers,
    serializeTable: serializeLayers,
  },
  [DXFSymbol.Style]: {
    tableRecordsProperty: TableKey.Styles,
    tableName: TableName.Style,
    dxfSymbolName: DXFSymbol.Style,
    parseTableRecords: parseStyles,
    serializeTable: serializeStyles,
  },

  //Limited Support: using generic parseSomeTable and serializeSomeTable
  [DXFSymbol.AppId]: {
    tableRecordsProperty: TableKey.AppId,
    tableName: TableName.AppId,
    dxfSymbolName: DXFSymbol.AppId,
    parseTableRecords: parseSomeTable,
    serializeTable: serializeSomeTable,
  },
  [DXFSymbol.BlockRecord]: {
    tableRecordsProperty: TableKey.BlockRecord,
    tableName: TableName.BlockRecord,
    dxfSymbolName: DXFSymbol.BlockRecord,
    parseTableRecords: parseSomeTable,
    serializeTable: serializeSomeTable,
  },
  [DXFSymbol.DimStyle]: {
    tableRecordsProperty: TableKey.DimStyle,
    tableName: TableName.DimStyle,
    dxfSymbolName: DXFSymbol.DimStyle,
    parseTableRecords: parseSomeTable,
    serializeTable: serializeSomeTable,
  },
  [DXFSymbol.UCS]: {
    tableRecordsProperty: TableKey.UCS,
    tableName: TableName.UCS,
    dxfSymbolName: DXFSymbol.UCS,
    parseTableRecords: parseSomeTable,
    serializeTable: serializeSomeTable,
  },
  [DXFSymbol.View]: {
    tableRecordsProperty: TableKey.View,
    tableName: TableName.View,
    dxfSymbolName: DXFSymbol.View,
    parseTableRecords: parseSomeTable,
    serializeTable: serializeSomeTable,
  },
};

const tableDefinitionValues = Object.values(tableDefinitions);

const tablePropertyFromCode = new Map<number, keyof IBaseTable>([
  [5, 'handle'],
  [330, 'ownerHandle'],
  [70, 'maxEntities'],
]);

const codeFromTableProperty = new Map<keyof IBaseTable, string>(
  Array.from(tablePropertyFromCode.entries()).map(([code, property]) => [
    property,
    `${code}`,
  ]),
);
//delete special cases that are are handled differently when serializing and parsing
tablePropertyFromCode.delete(70);

codeFromTableProperty.set('subMarker1', '100');
codeFromTableProperty.set('subMarker2', '100');
codeFromTableProperty.set('subMarker3', '100');
codeFromTableProperty.set('subMarker4', '100');

const parseTable = <T extends DXFTableSymbol>(
  scanner: DxfArrayScanner,
  tableType: DXFTableSymbol,
): ITable<T> => {
  const table = {} as ITable<T>;
  const { tableRecordsProperty, parseTableRecords } = tableDefinitions[
    tableType
  ] as TableDefinition<T>;
  let expectedCount = 0;

  let curr = scanner.next();
  while (!groupIs(curr, 0, DXFSymbol.EndTable)) {
    const property = tablePropertyFromCode.get(curr.code);
    if (property != null) {
      (table[property] as GroupValue) = curr.value;
    } else {
      //Special cases of setting properties
      switch (curr.code) {
        case 100:
          if (!table.subMarkerCount) {
            table.subMarkerCount = 1;
          } else {
            table.subMarkerCount++;
          }
          table[`subMarker${table.subMarkerCount}` as 'subMarker1'] =
            curr.value as string;
          break;
        case 70:
          table.maxEntities = expectedCount = curr.value as number;
          break;
        case 102:
          if (
            typeof curr.value === 'string' &&
            curr.value.startsWith('{') &&
            curr.value.length > 0
          ) {
            const name = curr.value.substring(1);
            if (!table.applicationGroups) table.applicationGroups = {};
            table.applicationGroups[name] = parseApplicationGroup(scanner);
          }
          break;
        case 0:
          if (curr.value === tableType) {
            (table[tableRecordsProperty] as TableValueFromDXFSymbol<T>) =
              parseTableRecords(scanner);
            scanner.rewind();
            curr = scanner.lastReadGroup as IGroup<GroupValue>;
            //All property cases need to call scanner.next(), except when `parseTablesRecords` is called, so rewind to make the following scanner.next()
            //to put scanner's pointer in correct position.
          } else {
            logUnhandledGroup(curr);
          }
          break;
        default:
          logUnhandledGroup(curr);
      }
    }
    curr = scanner.next();
  }
  const tableRecords = table[tableRecordsProperty];
  if (tableRecords) {
    const actualCount = Array.isArray(tableRecords)
      ? tableRecords.length
      : typeof tableRecords === 'object'
      ? Object.keys(tableRecords).length
      : undefined;
    if (expectedCount !== actualCount)
      log.warn(
        `Parsed ${actualCount} ${tableType}'s but expected ${expectedCount}`,
      );
  }
  scanner.next();
  return table;
};

export const serializeTable = function* <T extends DXFTableSymbol>(
  tableName: TableNameFromDXFSymbol<T>,
  table: ITable<T>,
): IterableIterator<string> {
  const tableDefinition = tableDefinitionValues.find(
    (td) => td.tableName === tableName,
  );
  if (!tableDefinition) {
    return;
  }

  //start of table
  yield '0';
  yield DXFSymbol.Table;

  //Table Name
  yield '2';
  yield tableDefinition.dxfSymbolName;

  for (const [property, value] of Object.entries(table) as [
    keyof IBaseTable,
    IBaseTable[keyof IBaseTable],
  ][]) {
    const code = codeFromTableProperty.get(property);
    if (code != null) {
      yield code;
      yield value as string;
    } else {
      switch (property) {
        case 'applicationGroups':
          yield* serializeApplicationDefinedGroups(
            value as ApplicationDefinedGroups,
          );
          break;
      }
    }
  }
  const { tableRecordsProperty, serializeTable } =
    tableDefinition as TableDefinition<T>;
  const tableRecords = table[
    tableRecordsProperty as TableKeyFromDXFSymbol<T>
  ] as TableValueFromDXFSymbol<T>;
  const entityCount = Array.isArray(tableRecords)
    ? tableRecords.length
    : typeof tableRecords === 'object'
    ? Object.keys(tableRecords).length
    : undefined;
  if (
    table.maxEntities != null &&
    entityCount &&
    table.maxEntities < entityCount
  ) {
    log.warn(
      `${tableRecordsProperty}'s has ${entityCount} but max entities declared was ${table.maxEntities}`,
    );
  }
  if (entityCount != null) {
    yield* serializeTable(tableRecords);
  }

  //end of table
  yield '0';
  yield DXFSymbol.EndTable;
};

export const parseTables = (scanner: DxfArrayScanner): ITables => {
  const tables: ITables = {};
  let curr = scanner.next();
  while (curr.value !== DXFSymbol.EOF) {
    if (groupIs(curr, 0, DXFSymbol.EndSec)) break;

    if (groupIs(curr, 0, DXFSymbol.Table)) {
      curr = scanner.next();
      const tableDefinition = tableDefinitions[curr.value as DXFTableSymbol];
      if (tableDefinition) {
        const { tableName } = tableDefinition;
        log.debug(curr.value + ' Table {');
        (tables[tableName] as ITable<DXFTableSymbol>) = parseTable(
          scanner,
          curr.value as DXFTableSymbol,
        );
        curr = scanner.lastReadGroup as IGroup<GroupValue>;
        log.debug('}');
      } else {
        log.debug('Unhandled Table ' + curr.value);
      }
    } else {
      // else ignored
      curr = scanner.next();
    }
  }

  scanner.next();
  return tables;
};

export const serializeTables = function* (
  tables: ITables,
): IterableIterator<string> {
  //section name
  yield '2';
  yield DXFSymbol.Tables;

  for (const [tableName, table] of Object.entries(tables) as [
    TableNameFromDXFSymbol<DXFTableSymbol>,
    ITable<DXFTableSymbol>,
  ][]) {
    yield* serializeTable(tableName, table);
  }
};
